import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldOff, Trash2, Plus, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "./shared";

export default function SuppressionList() {
  const [items, setItems] = useState([]);
  const [bounces, setBounces] = useState([]);
  const [email, setEmail] = useState("");
  const load = async () => { const [a, b] = await Promise.all([api.get("/suppressions"), api.get("/bounces")]); setItems(a.data); setBounces(b.data); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) return;
    try { await api.post("/suppressions", { email: email.trim(), reason: "manual" }); toast.success("Added to suppression list"); setEmail(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => {
    try { await api.delete(`/suppressions/${id}`); toast.success("Removed — contact can be emailed again"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <section className="border border-border rounded-md bg-card" data-testid="suppression-settings">
      <div className="p-5 border-b border-border">
        <div className="text-sm font-medium flex items-center gap-2"><ShieldOff className="w-4 h-4 text-muted-foreground" />Unsubscribes & suppression list</div>
        <div className="text-xs text-muted-foreground">Every sequence email carries a one-click unsubscribe link (and List-Unsubscribe headers). Hard bounces (5xx / mailer-daemon failures) are suppressed instantly; 3 soft bounces in 7 days also suppress. Suppressed addresses are never enrolled or sent to.</div>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex gap-2"><Input data-testid="suppression-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add an address to suppress..." className="h-8 text-sm" /><Button size="sm" variant="outline" onClick={add} disabled={!email.trim()} data-testid="suppression-add"><Plus className="w-3.5 h-3.5 mr-1.5" />Suppress</Button></div>
        <div className="border border-border rounded-md divide-y divide-border" data-testid="suppression-list">
          {items.map((x) => (
            <div key={x.id} className="flex items-center gap-3 px-3 py-2" data-testid={`suppression-${x.id}`}>
              <div className="min-w-0 flex-1"><div className="text-sm mono truncate">{x.email}</div><div className="text-[10px] mono text-muted-foreground">{x.reason} · {fmtTime(x.created_at)}</div></div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(x.id)} data-testid={`suppression-remove-${x.id}`} title="Remove from list"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          {items.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground" data-testid="suppression-empty">No suppressed addresses.</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5"><AlertOctagon className="w-3 h-3" />Recent bounces</div>
          <div className="border border-border rounded-md divide-y divide-border" data-testid="bounce-list">
            {bounces.slice(0, 8).map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-1.5 text-xs" data-testid={`bounce-${b.id}`}>
                <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border ${b.kind === "hard" ? "bg-rose-500/15 text-rose-500 border-rose-500/30" : "bg-amber-500/15 text-amber-500 border-amber-500/30"}`}>{b.kind}</span>
                <span className="mono truncate flex-1">{b.email}</span>
                <span className="text-muted-foreground truncate max-w-[16rem]" title={b.detail}>{b.source} · {b.detail}</span>
                {b.suppressed && <span className="text-rose-500">suppressed</span>}
                <span className="text-muted-foreground mono">{fmtTime(b.created_at)}</span>
              </div>
            ))}
            {bounces.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground" data-testid="bounce-empty">No bounces detected.</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
